import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography,
  Alert
} from '@mui/material';
import BigQueryDatasetSelector from 'components/bigquery/BigQueryDatasetSelector';
import BigQueryDatasetCreator from 'components/bigquery/BigQueryDatasetCreator';
import { useParams } from 'react-router-dom';
import { InlineEditableField } from 'components/editable';

const ListCutterTargetSelector = ({
  initialDataset = '',
  initialTable = '',
  onDetailsChange,
  sourceTable = '',
}) => {
  const { projectId } = useParams();
  const [targetDataset, setTargetDataset] = useState(() => {
    if (initialDataset) {
      return initialDataset;
    }
    return null;
  });

  const [targetTable, setTargetTable] = useState(() => {
    if (initialTable) {
      return initialTable;
    }
    const date = new Date();
    const dateSuffix = `__custom_list_${date.toISOString().slice(0,10).replace(/-/g, '')}`;
    return `${sourceTable}${dateSuffix}`;
  });
  
  const [isEditingTable, setIsEditingTable] = useState(false);
  const [editingTableName, setEditingTableName] = useState('');
  const [tableExistsWarning, setTableExistsWarning] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [datasetReadOnlyError, setDatasetReadOnlyError] = useState(null);
  const [isDatasetValid, setIsDatasetValid] = useState(true);

  const handleTargetDatasetChange = async (event, newValue) => {
    setTargetDataset(newValue);
    // Clear table exists warning when dataset changes
    setTableExistsWarning(null);
    
    // Check if dataset is read-only when a new dataset is selected
    if (newValue) {
      try {
        await checkDatasetReadOnly(newValue);
      } catch (error) {
        console.error("Error checking dataset permissions:", error);
        setDatasetReadOnlyError(`Error checking dataset permissions: ${error.message}`);
        setIsDatasetValid(false);
      }
    } else {
      // If no dataset is selected, clear errors
      setDatasetReadOnlyError(null);
      setIsDatasetValid(true);
    }
  };

  const checkDatasetReadOnly = async (datasetId) => {
    try {
      // Use the new dataset endpoint
      const response = await fetch(`/bigquery/dataset/${datasetId}/?project_id=${projectId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to get dataset details: ${response.status}`);
      }
      
      const datasetDetails = await response.json();
      
      if (datasetDetails.is_read_only) {
        setDatasetReadOnlyError(
          `Dataset <strong style="font-family: monospace; color: #d32f2f;">\`${datasetId}\`</strong> is read-only. ` +
          `Please select a different dataset or create a new one.`
        );
        setIsDatasetValid(false);
      } else {
        setDatasetReadOnlyError(null);
        setIsDatasetValid(true);
      }
    } catch (error) {
      throw error;
    }
  };

  const startEditingTable = () => {
    setEditingTableName(targetTable);
    setIsEditingTable(true);
  };

  const cancelEditingTable = () => {
    setIsEditingTable(false);
    setEditingTableName('');
  };

  const handleEditingTableNameChange = (event) => {
    setEditingTableName(event.target.value);
  };

  const checkTableExistsAndUpdate = async (newTableName) => {
    if (!targetDataset || !newTableName) {
      throw new Error("Dataset and table name are required");
    }
    
    try {
      const queryParams = new URLSearchParams({
        project_id: projectId,
        dataset_id: targetDataset
      });
      
      const url = `/bigquery/list-tables/?${queryParams.toString()}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to check if table exists: ${response.status} ${errorText}`);
      }
      
      const tablesList = await response.json();
      const tableExists = tablesList.includes(newTableName);
      
      if (tableExists) {
        setTableExistsWarning(`Table <Box component="span" sx={{ fontFamily: 'monospace', color: '#1976d2', fontWeight: 'bold' }}>
          ${targetDataset}.${newTableName}
        </Box> already exists and will be overwritten. If you do not wish to overwrite the existing table, enter a new name.`);
      } else {
        setTableExistsWarning(null);
      }
      
      // Update the table name 
      setTargetTable(newTableName);
    } catch (error) {
      setTableExistsWarning(`Error checking if table exists: ${error.message}`);
      throw error;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      checkTableExistsAndUpdate(editingTableName);
    }
  };

  // Handle successful dataset creation
  const handleDatasetCreated = (datasetName) => {
    setRefreshKey(prev => prev + 1); // Trigger dataset list refresh
    
    // Auto-select the newly created dataset
    setTimeout(() => {
      setTargetDataset(datasetName);
      // Clear any existing table exists warning
      setTableExistsWarning(null);
      // Check if the newly created dataset is read-only (should not be)
      checkDatasetReadOnly(datasetName).catch(error => {
        console.error("Error checking new dataset permissions:", error);
      });
    }, 500);
  };

  // Notify parent component of changes
  useEffect(() => {
    // Always notify parent of current values, even if dataset is null/empty
    // Pass both the validation status and tableExistsWarning
    onDetailsChange(targetDataset || '', targetTable, isDatasetValid, tableExistsWarning);
  }, [targetDataset, targetTable, onDetailsChange, isDatasetValid, tableExistsWarning]);

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 2, mt: 5 }}>
        Set Destination
      </Typography>
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 1 }}>
        Specify the dataset and table where your new list will be created.
      </Typography>
      
      {/* Dataset creation form */}
      <BigQueryDatasetCreator 
        projectId={projectId}
        onDatasetCreated={handleDatasetCreated}
      />
      
      {/* Dataset selector with refresh key to trigger re-fetch */}
      <BigQueryDatasetSelector
        key={refreshKey} // Force re-render when refreshKey changes
        selectedDataset={targetDataset}
        onSelectDataset={handleTargetDatasetChange}
        label="Destination Dataset ID"
      />
      
      {/* Display read-only dataset error */}
      {datasetReadOnlyError && (
        <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
          Dataset <Box component="span" sx={{ fontFamily: 'monospace', color: '#d32f2f', fontWeight: 'bold' }}>
            {targetDataset}
          </Box> is read-only. Please select a different dataset or create a new one.
        </Alert>
      )}
      
      {/* Table name with edit mode */}
      <Box sx={{ mt: 2 }}>
        <InlineEditableField
          value={targetTable}
          onSave={checkTableExistsAndUpdate}
          label="Name the table for your new list:"
          showEditButton={false}
          editOnClick={true}
          disabled={!targetDataset || !isDatasetValid} // Disable if no dataset selected or dataset is read-only
        />
        
        {tableExistsWarning && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Table <Box component="span" sx={{ fontFamily: 'monospace', color: '#1976d2', fontWeight: 'bold' }}>
              {`${targetDataset}.${targetTable}`}
            </Box> already exists and will be overwritten. If you do not wish to overwrite the existing table, enter a new name.
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default ListCutterTargetSelector;
